import type { FaceCandidateSummary } from './face';

/**
 * Customer measurements: what was measured, when, how, and how much to believe it.
 *
 * A measurement is a historical fact about a person on a date. It is never edited — a re-measure
 * is a new record — and an order that copied one keeps its own snapshot forever, because a lens was
 * ground to those numbers.
 */

/** How the figures were obtained. One record type for all of them; only this column differs. */
export const MeasurementSource = {
  /** Somebody used a ruler or a pupillometer and typed the result. */
  MANUAL: 'MANUAL',
  LIVE_CAMERA: 'LIVE_CAMERA',
  PHOTO: 'PHOTO',
  PHONE_QR: 'PHONE_QR',
} as const;
export type MeasurementSource = (typeof MeasurementSource)[keyof typeof MeasurementSource];

/** Only VOIDED is stored; "superseded" is a question the history already answers. */
export const MeasurementStatus = { ACTIVE: 'ACTIVE', VOIDED: 'VOIDED' } as const;
export type MeasurementStatus = (typeof MeasurementStatus)[keyof typeof MeasurementStatus];

/**
 * What established that a pixel distance means a millimetre.
 *
 * Required for any automated result that claims an absolute physical measurement — and irrelevant
 * to a face-shape classification, which is a ratio and needs no scale at all.
 */
export const CalibrationMethod = {
  /** No scale established. Valid for manual entry, and for automated results claiming no millimetres. */
  NONE: 'NONE',
  /** An object of known size in frame. An ID-1 card is 85.60 × 53.98 mm by standard. */
  REFERENCE_CARD: 'REFERENCE_CARD',
  /** Known focal length and sensor size for the handset, with a depth or face-size prior. */
  DEVICE_INTRINSICS: 'DEVICE_INTRINSICS',
  /** A prompted, verified pose at a held distance, refused when the pose drifts. */
  GUIDED_DISTANCE: 'GUIDED_DISTANCE',
  /**
   * An assumed iris-based scale reference: the horizontal iris (limbus) diameter taken as a population constant.
   *
   * It is an assumption about eyes in general, not an object measured in this frame and not a measurement of
   * this person's eye. It is NOT subject-specific professional calibration, and must never be described as one.
   * Every result from it is stored `referenceOnly`, which a database CHECK enforces.
   */
  IRIS_REFERENCE: 'IRIS_REFERENCE',
} as const;
export type CalibrationMethod = (typeof CalibrationMethod)[keyof typeof CalibrationMethod];

/**
 * A reading of a model's confidence, for the screen.
 *
 * Derived from `confidenceBasisPoints`, never stored — and deliberately absent for a manual entry,
 * which has no confidence to report. A high score means the model was sure, not that the
 * measurement is clinically correct.
 */
export const MeasurementQuality = {
  HIGH: 'HIGH',
  MEDIUM: 'MEDIUM',
  LOW: 'LOW',
  REJECTED: 'REJECTED',
} as const;
export type MeasurementQuality = (typeof MeasurementQuality)[keyof typeof MeasurementQuality];

/** The acquisition attempt, which may end without producing anything. */
export const MeasurementSessionStatus = {
  CREATED: 'CREATED',
  /** A QR has been issued and the phone has not opened it yet. */
  WAITING_FOR_DEVICE: 'WAITING_FOR_DEVICE',
  CAPTURING: 'CAPTURING',
  /** A candidate result is waiting for a person at the desk. The computing already happened on the capture device. */
  AWAITING_REVIEW: 'AWAITING_REVIEW',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
  EXPIRED: 'EXPIRED',
  CANCELLED: 'CANCELLED',
} as const;
export type MeasurementSessionStatus =
  (typeof MeasurementSessionStatus)[keyof typeof MeasurementSessionStatus];

/** States a session cannot leave. Only COMPLETED may carry a measurement. */
export const TERMINAL_SESSION_STATUSES: readonly MeasurementSessionStatus[] = [
  MeasurementSessionStatus.COMPLETED,
  MeasurementSessionStatus.FAILED,
  MeasurementSessionStatus.EXPIRED,
  MeasurementSessionStatus.CANCELLED,
];

/** What a session is trying to obtain. Decides what a completed result must carry. */
export const MeasurementPurpose = {
  PD: 'PD',
  FACE: 'FACE',
  BOTH: 'BOTH',
} as const;
export type MeasurementPurpose = (typeof MeasurementPurpose)[keyof typeof MeasurementPurpose];

export const ALLOWED_SESSION_TRANSITIONS: Record<
  MeasurementSessionStatus,
  readonly MeasurementSessionStatus[]
> = {
  CREATED: ['WAITING_FOR_DEVICE', 'CAPTURING', 'CANCELLED', 'EXPIRED', 'FAILED'],
  WAITING_FOR_DEVICE: ['CAPTURING', 'CANCELLED', 'EXPIRED', 'FAILED'],
  CAPTURING: ['AWAITING_REVIEW', 'CANCELLED', 'EXPIRED', 'FAILED'],
  // Review ends in acceptance, a retake, or the session ending; nothing about a person reviewing can "fail".
  AWAITING_REVIEW: ['COMPLETED', 'CAPTURING', 'CANCELLED', 'EXPIRED'],
  COMPLETED: [],
  FAILED: [],
  EXPIRED: [],
  CANCELLED: [],
};

export function canTransitionSession(
  from: MeasurementSessionStatus,
  to: MeasurementSessionStatus,
): boolean {
  return (ALLOWED_SESSION_TRANSITIONS[from] ?? []).includes(to);
}

/**
 * Turns a model's confidence into something a screen can show.
 *
 * Basis points rather than a percentage so the stored value is an exact integer, matching how tax
 * rates are already held. Null in, null out — a manual measurement has no confidence, and inventing
 * one would put a ruler and an estimate side by side as though they were the same kind of claim.
 */
export function measurementQualityOf(
  confidenceBasisPoints: number | null | undefined,
): MeasurementQuality | null {
  if (confidenceBasisPoints === null || confidenceBasisPoints === undefined) return null;

  if (confidenceBasisPoints >= 9_000) return MeasurementQuality.HIGH;
  if (confidenceBasisPoints >= 7_500) return MeasurementQuality.MEDIUM;
  if (confidenceBasisPoints >= 5_000) return MeasurementQuality.LOW;

  return MeasurementQuality.REJECTED;
}

/** Which PD a figure is. Stored with every PD value, and snapshotted with it onto a prescription. */
export const PdType = {
  /** Looking into the distance: what single-vision distance lenses are centred on. */
  DISTANCE: 'DISTANCE',
  /** Converged for reading. Entered by hand only; no automated near PD exists. */
  NEAR: 'NEAR',
} as const;
export type PdType = (typeof PdType)[keyof typeof PdType];

/**
 * Whether camera measurement exists for a tenant at all.
 *
 * Set by a platform administrator, never by the tenant. ENABLED is refused until the camera method
 * has passed the professional benchmark — finishing the implementation is not a reason to switch it on.
 */
export const CameraMeasurementMode = {
  OFF: 'OFF',
  /** Benchmark and internal pilot only. Results are marked as validation data and cannot reach a prescription. */
  VALIDATION: 'VALIDATION',
  ENABLED: 'ENABLED',
} as const;
export type CameraMeasurementMode =
  (typeof CameraMeasurementMode)[keyof typeof CameraMeasurementMode];

/** Coarse on purpose: enough to compare devices in the benchmark, never enough to identify one. */
export const CaptureDeviceClass = {
  IOS_SAFARI: 'IOS_SAFARI',
  ANDROID_CHROME: 'ANDROID_CHROME',
  DESKTOP_CHROMIUM: 'DESKTOP_CHROMIUM',
  DESKTOP_SAFARI: 'DESKTOP_SAFARI',
  OTHER: 'OTHER',
} as const;
export type CaptureDeviceClass = (typeof CaptureDeviceClass)[keyof typeof CaptureDeviceClass];

/** Platform maximums, in minutes. A tenant may shorten any of them and lengthen none. */
export const MEASUREMENT_TTL_LIMITS = {
  qrTokenTtlMinutes: 5,
  captureTtlMinutes: 15,
  reviewTtlMinutes: 30,
} as const;

/** What a tenant without a settings row gets: camera off, and the maximums as defaults. */
export const DEFAULT_MEASUREMENT_SETTINGS = {
  cameraMeasurementMode: CameraMeasurementMode.OFF,
  ...MEASUREMENT_TTL_LIMITS,
} as const;

/**
 * The lowest overall capture quality that may be put in front of a reviewer at all.
 *
 * Provisional. It keeps LOW and REJECTED captures out of review and out of storage; it does not say
 * that MEDIUM or HIGH is clinically acceptable. Which bands are eligible for production is for the
 * professional benchmark to decide.
 */
export const PROVISIONAL_REVIEW_FLOOR_BASIS_POINTS = 7_500;

/** Why an attempt ended below the floor. Codes only — the reading that failed is never kept. */
export const MeasurementAttemptOutcome = {
  QUALITY_TOO_LOW: 'QUALITY_TOO_LOW',
  NO_REFERENCE_CARD: 'NO_REFERENCE_CARD',
  WRONG_CARD_SIZE: 'WRONG_CARD_SIZE',
  POSE: 'POSE',
  BLUR: 'BLUR',
  LIGHTING: 'LIGHTING',
  MULTIPLE_FACES: 'MULTIPLE_FACES',
  TIMED_OUT: 'TIMED_OUT',
} as const;
export type MeasurementAttemptOutcome =
  (typeof MeasurementAttemptOutcome)[keyof typeof MeasurementAttemptOutcome];

/** Why a capture device gave up on a session. */
export const CaptureFailureReason = {
  CAMERA_DENIED: 'CAMERA_DENIED',
  CAMERA_UNAVAILABLE: 'CAMERA_UNAVAILABLE',
  UNSUPPORTED_BROWSER: 'UNSUPPORTED_BROWSER',
} as const;
export type CaptureFailureReason = (typeof CaptureFailureReason)[keyof typeof CaptureFailureReason];

/** Why a candidate never became a measurement. Carried by MEASUREMENT_REJECTED. */
export const MeasurementRejectionReason = {
  RETAKE_REQUESTED: 'RETAKE_REQUESTED',
  CANCELLED_AT_REVIEW: 'CANCELLED_AT_REVIEW',
  EXPIRED_AT_REVIEW: 'EXPIRED_AT_REVIEW',
} as const;
export type MeasurementRejectionReason =
  (typeof MeasurementRejectionReason)[keyof typeof MeasurementRejectionReason];

/** An algorithm version such as `pd-card-0.1.0` or `cardless-assisted-pd 0.2-prototype`: lower case, digits, dots and dashes, optionally a name and a version separated by one space. */
export const ALGORITHM_VERSION_PATTERN = /^[a-z0-9][a-z0-9.-]{1,39}( [a-z0-9][a-z0-9.-]{1,39})?$/;

/**
 * Calibration methods whose results may never be treated as authoritative.
 *
 * `IRIS_REFERENCE` scales the image by an assumed iris diameter rather than by anything measured in the frame, so the millimetres it produces are an estimate about a population, not about this person. Such a measurement is stored, shown and compared — never copied onto a prescription.
 */
export const REFERENCE_ONLY_CALIBRATION: readonly CalibrationMethod[] = [
  CalibrationMethod.IRIS_REFERENCE,
];

/** Whether a result from this calibration must carry `referenceOnly`. The database holds the same rule as a CHECK. */
export const isReferenceOnlyCalibration = (method: CalibrationMethod): boolean =>
  REFERENCE_ONLY_CALIBRATION.includes(method);

/** Millimetre fields. A value in any of these is an absolute physical claim needing calibration. */
export const ABSOLUTE_MEASUREMENT_FIELDS = [
  'rightPdMm',
  'leftPdMm',
  'totalPdMm',
  'faceWidthMm',
  'bridgeWidthMm',
  'eyeWidthMm',
  'faceLengthMm',
] as const;
export type AbsoluteMeasurementField = (typeof ABSOLUTE_MEASUREMENT_FIELDS)[number];

// ---------------------------------------------------------------------------
// Contracts
// ---------------------------------------------------------------------------

/** Every millimetre value crosses the wire as a decimal string. Never a JSON number. */
export interface MeasurementValues {
  rightPdMm: string | null;
  leftPdMm: string | null;
  totalPdMm: string | null;
  faceWidthMm: string | null;
  bridgeWidthMm: string | null;
  eyeWidthMm: string | null;
  faceLengthMm: string | null;
}

export interface CustomerMeasurementSummary extends MeasurementValues {
  id: string;
  customerId: string;
  branchId: string | null;
  branchName: string | null;
  sessionId: string | null;
  source: MeasurementSource;
  status: MeasurementStatus;
  /** Null exactly when no PD value was recorded. */
  pdType: PdType | null;
  /** The algorithm behind an automated result; null for a manual entry. */
  algorithmVersion: string | null;
  /** Benchmark data from VALIDATION mode. Never offered to, or accepted by, a prescription. */
  isValidation: boolean;
  /** An assisted estimate kept for comparison only: shown beside the technician's PD, never copied onto a prescription and never authoritative. */
  referenceOnly: boolean;
  measuredAt: string;
  measuredByName: string | null;
  calibrationMethod: CalibrationMethod;
  /** 0–10 000. Null for a manual entry — a ruler is not an estimate. */
  confidenceBasisPoints: number | null;
  /** Derived from the above. Null whenever the confidence is. */
  quality: MeasurementQuality | null;
  notes: string | null;
  voidedReason: string | null;
  /** True when a later active measurement exists for this customer. Derived, never stored. */
  isSuperseded: boolean;
}

/** The capture quality behind a candidate or an accepted result, each part in basis points. */
export interface CaptureQuality {
  landmarkBasisPoints: number;
  calibrationBasisPoints: number;
  poseBasisPoints: number;
  stabilityBasisPoints: number;
  /** The weakest of the four. */
  overallBasisPoints: number;
  band: MeasurementQuality;
  acceptedFrameCount: number;
}

/** A result waiting for review. Only ever read through the authenticated desk API, never streamed. */
export interface MeasurementCandidate {
  /** Null together with leftPdMm for a total-only result; never half the total. */
  rightPdMm: string | null;
  leftPdMm: string | null;
  /** Always present; with both eyes, exactly their sum. */
  totalPdMm: string;
  pdType: PdType;
  submittedAt: string;
}

export interface MeasurementSessionSummary {
  id: string;
  customerId: string;
  customerName: string;
  branchId: string;
  orderId: string | null;
  purpose: MeasurementPurpose;
  requestedSource: MeasurementSource;
  status: MeasurementSessionStatus;
  /** Created in VALIDATION mode: whatever it produces is benchmark data. */
  isValidation: boolean;
  /** Present only while awaiting review. PD sessions only. */
  candidate: MeasurementCandidate | null;
  /** The face suggestion waiting for a person. FACE sessions only, and only while awaiting review. */
  faceCandidate: FaceCandidateSummary | null;
  /** Present while awaiting review and after completion. */
  quality: CaptureQuality | null;
  /** How the candidate established millimetres; null until one is submitted. */
  calibrationMethod: CalibrationMethod | null;
  /** True once a candidate arrives from a calibration that may only be compared, never prescribed. */
  referenceOnly: boolean;
  algorithmVersion: string | null;
  deviceClass: CaptureDeviceClass | null;
  attemptCount: number;
  lastOutcome: string | null;
  resultMeasurementId: string | null;
  failureReason: string | null;
  createdAt: string;
  expiresAt: string;
  completedAt: string | null;
}

/** The HttpOnly cookie that carries the capture capability, and the only path it is ever sent to. */
export const CAPTURE_COOKIE_NAME = 'optiflow_capture';
export const CAPTURE_COOKIE_PATH = '/api/v1/capture';

/**
 * Everything a capture device may know about its session.
 *
 * No customer, no order, no prescription, no measured value — the phone shows "Ready to measure"
 * and capture guidance, and the desk does the reviewing.
 */
export interface CaptureSessionView {
  status: MeasurementSessionStatus;
  purpose: MeasurementPurpose;
  expiresAt: string;
  /**
   * Whether this session is part of a supervised validation pilot.
   *
   * Session metadata, not anything about a person, and the phone needs it for one reason: a study
   * operator must be able to read the machine's own capture outcome off the device instead of
   * guessing it from the screen. Outside VALIDATION nothing is shown and nothing changes.
   */
  isValidation: boolean;
}

/** What the desk receives when it asks for a code. The token appears here once and is never stored. */
export interface MeasurementQrCode {
  session: MeasurementSessionSummary;
  /** The capture page, with the one-time token in the fragment — never sent to any server by a browser. */
  qrUrl: string;
  tokenExpiresAt: string;
}

export interface MeasurementSettingsSummary {
  cameraMeasurementMode: CameraMeasurementMode;
  /** Camera face analysis, released through the same three states and the same platform-only control. */
  faceAnalysisMode: CameraMeasurementMode;
  qrTokenTtlMinutes: number;
  captureTtlMinutes: number;
  reviewTtlMinutes: number;
  /** The platform maximums the three TTLs can never exceed. */
  limits: typeof MEASUREMENT_TTL_LIMITS;
}
